import { motion } from "framer-motion";
import { Check, Clock, Sparkles, FileText, Lock, Wallet,
         Truck, ScrollText, ShieldCheck, Trophy } from "lucide-react";

/**
 * Vinci Order Express · 8-Milestone visual tracker.
 *
 * Renders the buyer-facing pulse of an RFQ → Order:
 *   1. RFQ Filed         (Intake)
 *   2. Quote Locked      (Pricing · 48h Price Lock)
 *   3. Booking Received  (Security · 30% booking fee)
 *   4. Logistics Sync    (Sourcing · supplier assignment)
 *   5. Final Settlement  (Payment · 70% balance)
 *   6. Out for Delivery  (Transit · LR / E-Way Bill)
 *   7. Quality Verified  (Compliance · MTC uploaded)
 *   8. Trade Completed   (Finality · commission booked)
 *
 * Props:
 *   currentStage : 1-8   (the milestone in progress)
 *   completed    : Set<number>  (explicit set of completed stage numbers)
 *   erroredAt    : number|null  (stage that has a warning/error)
 *   compact      : bool  (horizontal strip · used on success screens)
 */
export const ORDER_MILESTONES = [
  { n: 1, icon: FileText,   title: "RFQ Filed",         sub: "Intake · SRN minted", detail: "Your request is officially recorded on the Vinci ledger with a unique SRN." },
  { n: 2, icon: Lock,       title: "Quote Locked",      sub: "48h Price Lock",       detail: "Our trade desk fixes the daily rate. Awaiting your commitment." },
  { n: 3, icon: Wallet,     title: "Booking Received",  sub: "30% security fee",     detail: "Capital secured via PhonePe, Razorpay or Wallet. Materials reserved for you." },
  { n: 4, icon: Sparkles,   title: "Logistics Sync",    sub: "Supplier assigned",    detail: "Your supply-chain partner is preparing the consignment." },
  { n: 5, icon: ShieldCheck,title: "Final Settlement",  sub: "70% balance",          detail: "Tax invoice generated. Financial pulse complete." },
  { n: 6, icon: Truck,      title: "Out for Delivery",  sub: "LR / E-Way Bill",      detail: "Consignment has left the factory — live status visible on the tracker." },
  { n: 7, icon: ScrollText, title: "Quality Verified",  sub: "MTC vault",            detail: "Material Test Certificate signed to your blueprint spec." },
  { n: 8, icon: Trophy,     title: "Trade Completed",   sub: "Finalised",            detail: "Both parties finalised. Trade closed in the master history." },
];


export function OrderMilestoneTracker({ currentStage = 1, completed,
                                             erroredAt = null, compact = false }) {
  const done = completed instanceof Set ? completed : new Set(
    Array.from({ length: (currentStage || 0) - 1 }, (_, i) => i + 1));

  const state = (n) => {
    if (erroredAt === n) return "error";
    if (done.has(n)) return "done";
    if (n === currentStage) return "current";
    return "upcoming";
  };
  const palette = {
    done:     { ring: "#2FB999", text: "#F5F5F0", label: "Complete" },
    current:  { ring: "#D8B23E", text: "#F6E4A6", label: "In progress" },
    upcoming: { ring: "#3a3a35", text: "#8A8A82", label: "Upcoming" },
    error:    { ring: "#C0392B", text: "#F8B5A8", label: "Attention" },
  };

  if (compact) {
    return (
      <div data-testid="order-milestones-strip"
            className="flex items-stretch gap-2 overflow-x-auto py-4">
        {ORDER_MILESTONES.map((m) => {
          const s = state(m.n);
          const p = palette[s];
          return (
            <motion.div
              key={m.n}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 * m.n }}
              data-testid={`milestone-${m.n}-${s}`}
              className="flex-1 min-w-[130px] border-t-2 pt-3 px-2"
              style={{ borderColor: p.ring }}
            >
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[9px]" style={{ color: p.ring }}>0{m.n}</span>
                {s === "done" && <Check className="w-3 h-3 text-[#2FB999]" />}
                {s === "current" && <span className="w-2 h-2 rounded-full bg-[#D8B23E] animate-pulse" />}
              </div>
              <div className="mt-1 font-grotesk text-[10px] uppercase tracking-[0.2em]"
                    style={{ color: p.text }}>{m.title}</div>
              <div className="text-[10px] mt-0.5" style={{ color: p.ring }}>{m.sub}</div>
            </motion.div>
          );
        })}
      </div>
    );
  }

  return (
    <ol data-testid="order-milestones-list" className="space-y-4">
      {ORDER_MILESTONES.map((m) => {
        const s = state(m.n);
        const p = palette[s];
        const Icon = m.icon;
        return (
          <motion.li
            key={m.n}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.05 * m.n }}
            data-testid={`milestone-row-${m.n}-${s}`}
            className="flex gap-4 items-start p-4 border rounded-sm bg-black/20"
            style={{ borderColor: `${p.ring}55` }}
          >
            <div className="grid place-items-center rounded-full w-10 h-10 shrink-0 border"
                  style={{ borderColor: p.ring,
                            background: s === "current" ? `${p.ring}22` : "transparent" }}>
              {s === "done" ? <Check className="w-4 h-4 text-[#2FB999]" />
                              : <Icon className="w-4 h-4" style={{ color: p.ring }} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px]" style={{ color: p.ring }}>MILESTONE 0{m.n}</span>
                <span className="font-grotesk text-[9px] uppercase tracking-[0.24em]"
                       style={{ color: p.text }}>{p.label}</span>
              </div>
              <div className="mt-1 font-serif text-lg" style={{ color: p.text }}>{m.title}</div>
              <div className="text-xs text-[#8A8A82] mt-0.5">{m.sub}</div>
              <div className="text-sm text-[#C9C9C2] mt-2 leading-relaxed">{m.detail}</div>
            </div>
          </motion.li>
        );
      })}
    </ol>
  );
}
